import { MISSION_ENUM_TYPES, MISSION_STATUS_ENUM } from 'shared/constants'
import { theme } from 'Assets/Styles/theme'
import { MarkerModal, MarkerModalWithTabs } from './MarkerModal'
import {
  LiveMissions,
  TodayMission,
  TomorrowMissions,
  YesterdayMissions,
} from './filters'

const GOOGLE_FORWARD_CLOSED_ARROW = 1
const GOOGLE_FORWARD_OPEN_ARROW = 2

export const getMissionZoneShapeProps = ({ id, boundary, boundaryColour }) => {
  if (!boundary || boundary.length <= 0) return
  return {
    key: id,
    path: boundary.map(path => ({
      lat: path.latitude,
      lng: path.longitude,
    })),
    options: {
      fillColor: boundaryColour,
      strokeColor: boundaryColour,
      fillOpacity: 0.3,
    },
  }
}

export const getMissionTypeSpecificProps = mission => {
  if (mission.type === MISSION_ENUM_TYPES.LETTERBOX) {
    return {
      pathColor: theme.colors.black,
      polygon: getMissionZoneShapeProps({
        id: mission.id,
        boundary: mission.boundary,
        boundaryColour: mission.boundaryColour,
      }),
    }
  }
  return {}
}

export const getMissionDotColor = status => {
  switch (status) {
    case MISSION_STATUS_ENUM.AVAILABLE:
      return theme.colors.curiousBlue
    case MISSION_STATUS_ENUM.BOOKED:
      return theme.colors.rebeccaPurple
    case MISSION_STATUS_ENUM.CHECKED_IN:
      return theme.colors.green
    case MISSION_STATUS_ENUM.CANCELLED:
      return theme.colors.regentGray
    default:
      return theme.colors.regentGray
  }
}

export const getLocation = mission => {
  const {
    location,
    boundary,
    checkInLocation,
    checkInTime,
    checkoutTime,
    checkoutLocation,
    lastActivity,
    status,
  } = mission

  // mission finished
  if (checkoutLocation && checkoutTime) {
    // add last position
    return {
      lat: parseFloat(checkoutLocation.latitude),
      lng: parseFloat(checkoutLocation.longitude),
    }
  }

  // mission in progress
  if (status === MISSION_STATUS_ENUM.CHECKED_IN && lastActivity) {
    return {
      lat: parseFloat(lastActivity.latitude),
      lng: parseFloat(lastActivity.longitude),
    }
  }

  // mission started
  if (checkInTime && checkInLocation) {
    // get checkin position
    return {
      lat: parseFloat(checkInLocation.latitude),
      lng: parseFloat(checkInLocation.longitude),
    }
  }

  // mission unstarted
  if (location && location.latitude && location.longitude) {
    return {
      lat: parseFloat(location.latitude),
      lng: parseFloat(location.longitude),
    }
  }

  if (boundary && boundary.length) {
    return {
      lat: parseFloat(boundary[0].latitude),
      lng: parseFloat(boundary[0].longitude),
    }
  }
}

export const getMissionMarker = (mission, cityId) => {
  const {
    location,
    boundary,
    checkInLocation,
    checkInTime,
    checkoutTime,
    checkoutLocation,
    lastActivity,
    status,
    id,
  } = mission
  const CIRCLE = 0
  const color = getMissionDotColor(status)
  const position = getLocation(mission)

  // mission finished
  if (checkoutLocation && checkoutTime) {
    // add last position
    return {
      position,
      options: {
        infoWindowSettings: {
          content: MarkerModal({ mission, cityId }),
        },
        icon: {
          path: CIRCLE,
          scale: 8,
          strokeColor: color,
        },
      },
      customProperties: {
        missionId: id,
      },
    }
  }

  // mission in progress
  if (status === MISSION_STATUS_ENUM.CHECKED_IN && lastActivity) {
    // get last position from activities
    return {
      position,
      options: {
        infoWindowSettings: {
          content: MarkerModal({ mission, cityId }),
        },
        icon: {
          path: CIRCLE,
          scale: 8,
          strokeColor: color,
        },
      },
      customProperties: {
        missionId: id,
      },
    }
  }

  // mission started
  if (checkInTime && checkInLocation) {
    // get checkin position
    return {
      position,
      options: {
        infoWindowSettings: {
          content: MarkerModal({ mission, cityId }),
        },
        icon: {
          path: CIRCLE,
          scale: 8,
          strokeColor: color,
        },
      },
      customProperties: {
        missionId: id,
      },
    }
  }

  // mission unstarted
  if (location && location.latitude && location.longitude) {
    return {
      position,
      options: {
        infoWindowSettings: {
          content: MarkerModal({ mission, cityId }),
        },
        icon: {
          path: CIRCLE,
          scale: 8,
          strokeColor: color,
        },
      },
      customProperties: {
        missionId: id,
      },
    }
  }

  if (boundary && boundary.length) {
    return {
      position,
      options: {
        infoWindowSettings: {
          content: MarkerModal({ mission, cityId }),
        },
        icon: {
          path: CIRCLE,
          scale: 8,
          strokeColor: color,
        },
      },
      customProperties: {
        missionId: id,
      },
    }
  }
}

export const getMissionMarkerWithTabs = (missions, cityId) => {
  const { location, boundary, status } = missions[0]

  const CIRCLE = 0
  const color = getMissionDotColor(status)

  if (location && location.latitude && location.longitude) {
    return {
      position: {
        lat: parseFloat(location.latitude),
        lng: parseFloat(location.longitude),
      },
      options: {
        infoWindowSettings: {
          content: MarkerModalWithTabs({ missions, cityId }),
        },
        icon: {
          path: CIRCLE,
          scale: 8,
          strokeColor: color,
        },
      },
    }
  }

  if (boundary && boundary.length) {
    return {
      position: {
        lat: parseFloat(boundary[0].latitude),
        lng: parseFloat(boundary[0].longitude),
      },
      options: {
        infoWindowSettings: {
          content: MarkerModalWithTabs({ missions, cityId }),
        },
        icon: {
          path: CIRCLE,
          scale: 8,
          strokeColor: color,
        },
      },
    }
  }
}

export const getMissionPath = ({
  activities,
  checkInLocation,
  checkInTime,
  checkoutLocation,
  checkoutTime,
  pathColor,
}) => {
  let path = []

  if (
    checkInLocation &&
    checkInLocation.latitude &&
    checkInLocation.longitude
  ) {
    path = [
      {
        lat: parseFloat(checkInLocation.latitude),
        lng: parseFloat(checkInLocation.longitude),

        label: new Date(checkInTime).toLocaleTimeString(),
      },
    ]
  }

  path = [
    ...path,
    ...(activities || []).map(activityItem => {
      return {
        lat: parseFloat(activityItem.latitude),
        lng: parseFloat(activityItem.longitude),
        label: new Date(activityItem.datetime).toLocaleTimeString(),
      }
    }),
  ]

  if (
    checkoutLocation &&
    checkoutLocation.latitude &&
    checkoutLocation.longitude
  ) {
    path.push({
      lat: parseFloat(checkoutLocation.latitude),
      lng: parseFloat(checkoutLocation.longitude),
      label: new Date(checkoutTime).toLocaleTimeString(),
    })
  }

  return {
    path,
    options: {
      strokeColor: pathColor,
      strokeOpacity: 1.0,
      strokeWeight: 2,
      infoWindowSettings: {
        content: 'Movements Path',
      },
      icons: [
        {
          icon: {
            path: GOOGLE_FORWARD_OPEN_ARROW,
            scale: 1.5,
          },
          repeat: '70px',
          offset: '100%',
        },
        {
          icon: {
            path: GOOGLE_FORWARD_CLOSED_ARROW,
            strokeColor: '#F00',
            fillColor: '#fff',
            fillOpacity: 1,
            scale: 5,
          },
          offset: '100%',
        },
      ],
    },
  }
}

export const getTimePresetsMessage = timePreset => {
  switch (timePreset) {
    case TodayMission:
      return 'Note that only missions happening today are displayed on the map'
    case YesterdayMissions:
      return 'Note that only missions happening yesterday are displayed on the map'
    case TomorrowMissions:
      return 'Note that only missions happening tomorrow are displayed on the map'
    case LiveMissions:
      return 'Note that only missions happening in more/less 3h are displayed on the map'
  }
}



// WEBPACK FOOTER //
// ./src/Components/LiveMap/utils.js